/**
 * CRMShell.jsx — Phase 69 + 70
 *
 * Standalone CRM module. Promoted out of /studio into a top-level /crm route
 * so BD/sales teammates can be granted individual access without seeing the
 * rest of Studio (finance / contracts / vendors / etc).
 *
 * Architecture: single-page tabbed shell. Each tab is a sub-component with
 * its own data fetch + state. URL `?tab=…` keeps deep links stable.
 *
 * Ships:
 *   · Dashboard         — funnel KPIs
 *   · Target Accounts   — inline table, bulk delete, convert-to-prospect
 *   · Prospects         — inline table, bulk delete, convert-to-lead
 *   · Leads (Kanban)    — native HTML5 drag-and-drop board, auto-creates a
 *                         Deal stub when a lead lands in "Proposal Requested"
 *   · Deals / Clients / Reports / Settings — coming in Phase 71-72
 */
import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, StatCard } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";
import usePermissions from "@/lib/usePermissions";
import { toast } from "sonner";
import {
  LayoutDashboard, Users, Inbox, Briefcase, UserCheck, BarChart3, Settings,
  Search, Plus, Trash2, ArrowRight, Loader2, Upload, X, FileSignature, Pencil, Lock, Quote,
} from "lucide-react";

// Phase 75 — sub-modules extracted from this monolith into /pages/crm/*.jsx.
// Phase 84.4 — Studio "Clients" section merged into CRM. The Clients tab now
// renders AdminClients (the full-featured client directory that includes
// Name/Company/Title/Website/Phone/Tag/Primary POC + multi-POC panel + Notes
// + invite flow). The old sales-snapshot table has been retired.
import AdminClients from "@/pages/AdminClients";
import Contracts from "@/pages/Contracts";
import {
  Loading, InlineText,
  LEAD_STAGES, STAGE_TONES, stageToneFor, useLeadStages, LEAD_TYPES, SERVICES, ICP_OPTIONS, LEAD_STATUSES,
  LEAD_TYPE_TONE, ICP_TONE, inr, daysAgo,
} from "./crm/_shared";
import { LeadCard } from "./crm/LeadCard";
import { LeadDetailDrawer } from "./crm/LeadDetailDrawer";
// Phase 82.1 — CRM ActivityTimeline retired; all CRM audit events surface
// via the main /admin/audit-log page.
import { CommandPalette } from "./crm/CommandPalette";
import { SettingsTab } from "./crm/SettingsTab";

/* -------------------------------------------------------------------------- */
/*  Tab catalog                                                                */
/* -------------------------------------------------------------------------- */

const TABS = [
  { id: "dashboard",       label: "Dashboard",       Icon: LayoutDashboard },
  { id: "prospects",       label: "Prospects",       Icon: Users },
  { id: "leads",           label: "Leads",           Icon: Inbox },
  { id: "deals",           label: "Deals",           Icon: Briefcase },
  { id: "clients",         label: "Clients",         Icon: UserCheck },
  { id: "contracts",       label: "Contracts",       Icon: FileSignature },
  { id: "testimonials",    label: "Testimonials",    Icon: Quote },
  { id: "reports",         label: "Reports",         Icon: BarChart3 },
  { id: "settings",        label: "Settings",        Icon: Settings },
];

export default function CRMShell() {
  const [params, setParams] = useSearchParams();
  const active = params.get("tab") || "dashboard";
  const setActive = (id) => setParams({ tab: id }, { replace: true });

  return (
    <div className="p-4 sm:p-8 max-w-7xl mx-auto fade-in" data-testid="crm-shell">
      <PageHeader
        title="CRM"
        eyebrow="Business Development"
        subtitle="Prospects → leads → deals → clients. All in one place."
      />

      {/* Tab strip */}
      <div className="flex items-center gap-1 overflow-x-auto pb-1 mb-5 border-b border-slate-200" data-testid="crm-tabs">
        {TABS.map(({ id, label, Icon, comingSoon }) => (
          <button
            key={id}
            type="button"
            onClick={() => setActive(id)}
            className={`shrink-0 inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-t-sm border-b-2 whitespace-nowrap transition-colors ${
              active === id
                ? "border-[#F54800] text-slate-900 font-medium"
                : "border-transparent text-slate-500 hover:text-slate-800 hover:bg-slate-50"
            }`}
            data-testid={`crm-tab-${id}`}
          >
            <Icon size={14} />
            {label}
            {comingSoon && <span className="text-[9px] uppercase tracking-wider text-slate-400 ml-1">soon</span>}
          </button>
        ))}
      </div>

      {/* Active tab */}
      {active === "dashboard"       && <CRMDashboardTab />}
      {active === "prospects"       && <ProspectsTab />}
      {active === "leads"           && <LeadsKanbanTab />}
      {active === "deals"           && <DealsTab />}
      {active === "clients"         && <AdminClients embedded />}
      {active === "contracts"       && <Contracts embedded />}
      {active === "testimonials"    && <TestimonialsTab />}
      {active === "reports"         && <ReportsTab />}
      {active === "settings"        && <SettingsTab />}

      {/* Phase 74 — global Cmd+K palette + intake-token quick UI */}
      <CommandPalette />
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Dashboard tab                                                              */
/* -------------------------------------------------------------------------- */
function CRMDashboardTab() {
  const [stats, setStats] = useState(null);
  const [funnel, setFunnel] = useState(null);
  const [velocity, setVelocity] = useState(null);
  const [pipeline, setPipeline] = useState(null);
  useEffect(() => {
    Promise.all([
      api.get("/crm/stats").then(({ data }) => setStats(data)).catch(() => setStats({})),
      api.get("/crm/reports/funnel").then(({ data }) => setFunnel(data)).catch(() => setFunnel(null)),
      api.get("/crm/reports/velocity").then(({ data }) => setVelocity(data)).catch(() => setVelocity(null)),
      api.get("/finance/contract-pipeline").then(({ data }) => setPipeline(data)).catch(() => setPipeline(null)),
    ]);
  }, []);
  if (!stats) return <Loading />;
  // Phase 74: ₹ everywhere across the CRM.
  const fmt = inr;
  return (
    <div data-testid="crm-dashboard-tab">
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 mb-4">
        <StatCard label="Prospects" value={stats.prospects || 0} accent="text-[#F54800]" data-testid="crm-stat-prospects" />
        <StatCard label="Contacted" value={stats.prospects_contacted || 0} data-testid="crm-stat-contacted" />
        <StatCard label="Leads" value={stats.leads || 0} data-testid="crm-stat-leads" />
        <StatCard label="Deals" value={stats.deals || 0} data-testid="crm-stat-deals" />
        <StatCard label="Won" value={stats.won || 0} accent="text-emerald-600" data-testid="crm-stat-won" />
      </div>
      {/* Phase 71 — Deal pipeline value cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <StatCard label="Pipeline value" value={fmt(stats.pipeline_value || 0)} hint="Open deals · sum of proposal_value" accent="text-slate-900" data-testid="crm-stat-pipeline-value" />
        <StatCard label="Won value" value={fmt(stats.won_value || 0)} hint={`${stats.deals_won || 0} won deals`} accent="text-emerald-600" data-testid="crm-stat-won-value" />
        <StatCard label="Deals won" value={stats.deals_won || 0} accent="text-emerald-600" data-testid="crm-stat-deals-won" />
        <StatCard label="Deals lost" value={stats.deals_lost || 0} accent="text-rose-600" data-testid="crm-stat-deals-lost" />
      </div>

      {/* Phase 96 — Contract Pipeline, relocated here from the Finance page. */}
      {pipeline && pipeline.total_contracts > 0 && (
        <div className="card-flat mb-6" data-testid="contract-pipeline">
          <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
            <div className="font-display font-bold inline-flex items-center gap-2">
              <FileSignature size={16} className="text-[#F54800]" /> Contract pipeline
            </div>
            <a href="/crm?tab=contracts" className="text-xs text-[#F54800] hover:underline">Manage →</a>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 px-5 py-5">
            <div data-testid="pipeline-open">
              <div className="label-overline">Open pipeline</div>
              <div className="font-display text-2xl font-bold text-slate-900 mt-1">{fmt(pipeline.pipeline_value)}</div>
            </div>
            <div data-testid="pipeline-signed">
              <div className="label-overline">Signed (won)</div>
              <div className="font-display text-2xl font-bold text-green-700 mt-1">{fmt(pipeline.won_value)}</div>
            </div>
            <div data-testid="pipeline-overdue">
              <div className="label-overline">Overdue {">"}15d</div>
              <div className={`font-display text-2xl font-bold mt-1 ${pipeline.overdue_count > 0 ? "text-red-700" : "text-slate-400"}`}>{fmt(pipeline.overdue_value)}</div>
            </div>
            <div>
              <div className="label-overline">Total contracts</div>
              <div className="font-display text-2xl font-bold text-slate-900 mt-1">{pipeline.total_contracts}</div>
            </div>
          </div>
        </div>
      )}

      {/* Phase 72 — Funnel visualization */}
      {funnel && <FunnelChart funnel={funnel} />}

      {/* Phase 72 — Velocity metrics */}
      {velocity && (
        <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="crm-velocity-block">
          <VelocityCard label="Avg days · Proposal Sent → Won" value={velocity.avg_days_proposal_sent_to_won} testId="crm-velocity-proposal-to-won" />
          <VelocityCard label="Avg days · Deal Created → Won" value={velocity.avg_days_deal_created_to_won} testId="crm-velocity-deal-to-won" />
          <VelocityCard label="Avg days · MSA Signed → Won"   value={velocity.avg_days_msa_signed_to_won}    testId="crm-velocity-msa-to-won" />
        </div>
      )}

      {/* Phase 82.1 — CRM Recent activity panel retired. All CRM actions still
          write to the main audit_log (accessible via /admin/audit-log) so
          nothing is lost — the panel just no longer duplicates that here. */}
    </div>
  );
}

/* ---- Dashboard helpers (Phase 72) ---- */
function VelocityCard({ label, value, testId }) {
  const display = value === null || value === undefined
    ? <span className="text-slate-300 italic text-base">—</span>
    : <><span className="font-display text-2xl font-bold text-slate-900">{value}</span><span className="text-sm text-slate-500 ml-1">days</span></>;
  return (
    <div className="card-flat p-4" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">{label}</div>
      <div className="leading-tight">{display}</div>
    </div>
  );
}

function FunnelChart({ funnel }) {
  const steps = funnel.steps || [];
  if (steps.length === 0) return null;
  const maxCount = Math.max(...steps.map((s) => s.count), 1);
  // Color per step — descends in saturation to evoke "narrowing funnel"
  const colors = [
    "bg-[#F54800]",       // accounts (brand)
    "bg-orange-400",
    "bg-amber-400",
    "bg-blue-400",
    "bg-purple-400",
    "bg-emerald-500",     // won
  ];
  return (
    <div className="card-flat p-5" data-testid="crm-funnel-chart">
      <div className="flex items-end justify-between mb-3">
        <div>
          <div className="font-display text-base font-semibold text-slate-900">Conversion funnel</div>
          <div className="text-xs text-slate-500">Prospects → Leads → Deals → Won</div>
        </div>
        <div className="text-xs text-slate-500">
          Overall conversion: <span className="font-bold text-slate-900" data-testid="crm-funnel-overall">{funnel.overall_conversion ?? 0}%</span>
        </div>
      </div>
      <div className="space-y-2">
        {steps.map((s, i) => {
          const width = `${Math.max((s.count / maxCount) * 100, 6)}%`;
          const conv = s.conv_from_prev;
          // Cap conversion at 999% just so the badge doesn't explode visually
          const convDisplay = conv === null || conv === undefined ? null : `${conv > 999 ? "999+" : conv}%`;
          return (
            <div key={s.key} className="flex items-center gap-3" data-testid={`crm-funnel-step-${s.key}`}>
              <div className="w-32 text-xs text-slate-700 font-medium shrink-0">{s.label}</div>
              <div className="flex-1 relative h-8 bg-slate-50 rounded-sm overflow-hidden border border-slate-200">
                <div
                  className={`h-full ${colors[i] || "bg-slate-400"} flex items-center px-3 transition-all`}
                  style={{ width }}
                >
                  <span className="text-white text-xs font-bold">{s.count}</span>
                </div>
              </div>
              <div className="w-24 text-right shrink-0">
                {convDisplay ? (
                  <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-sm ${conv >= 30 ? "bg-emerald-100 text-emerald-700" : conv >= 10 ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-600"}`} title={`Conversion from ${steps[i-1]?.label || "—"}`}>
                    {convDisplay}
                  </span>
                ) : (
                  <span className="text-[10px] text-slate-300">—</span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Prospects tab                                                              */
/* -------------------------------------------------------------------------- */
const PROSPECT_STATUSES = [
  "Not Contacted", "Contacted", "Replied", "Interested", "Follow-up Needed",
  "Nurturing", "Converted to Lead", "Blacklisted",
];

function ProspectsTab() {
  const { user } = useAuth();
  const canEdit = user?.role !== "client" && user?.role !== "vendor";
  const [rows, setRows] = useState(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [tagFilter, setTagFilter] = useState("");
  const [tagList, setTagList] = useState([]);
  const [selected, setSelected] = useState(() => new Set());
  const [creating, setCreating] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/crm/prospects", { params: { q: search || undefined, status: statusFilter || undefined, tag: tagFilter || undefined } });
      setRows(data || []);
    } catch (e) { toast.error(formatApiError(e)); }
  }, [search, statusFilter, tagFilter]);

  const loadTags = useCallback(async () => {
    try {
      const { data } = await api.get("/crm/prospects/tags");
      setTagList(data.tags || []);
    } catch { /* non-fatal */ }
  }, []);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);
  useEffect(() => { loadTags(); }, [loadTags, rows?.length]);

  const addRow = async () => {
    setCreating(true);
    try {
      const { data } = await api.post("/crm/prospects", { full_name: "New prospect", status: "Not Contacted" });
      setRows((r) => [data, ...(r || [])]);
      toast.success("Prospect added");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setCreating(false); }
  };

  const patchField = async (id, field, value) => {
    setRows((r) => r.map((x) => x.id === id ? { ...x, [field]: value } : x));
    try { await api.patch(`/crm/prospects/${id}`, { [field]: value }); }
    catch (e) { toast.error(formatApiError(e)); load(); }
  };

  const removeRow = async (id) => {
    if (!window.confirm("Delete this prospect?")) return;
    setRows((r) => r.filter((x) => x.id !== id));
    try { await api.delete(`/crm/prospects/${id}`); }
    catch (e) { toast.error(formatApiError(e)); load(); }
  };

  const bulkDelete = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`Delete ${selected.size} prospects?`)) return;
    try {
      await api.post("/crm/prospects/bulk-delete", { ids: Array.from(selected) });
      toast.success(`${selected.size} deleted`);
      setSelected(new Set());
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const convert = async (id) => {
    try {
      await api.post(`/crm/prospects/${id}/convert-to-lead`);
      toast.success("Converted to lead — see Leads tab");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!rows) return <Loading />;

  return (
    <div data-testid="crm-prospects-tab">
      <Toolbar
        search={search} onSearch={setSearch}
        statusOptions={PROSPECT_STATUSES} status={statusFilter} onStatus={setStatusFilter}
        onAdd={canEdit ? addRow : null} adding={creating}
        selectedCount={selected.size} onBulkDelete={bulkDelete}
        onBulkUpload={canEdit ? () => setBulkOpen(true) : null}
        testIdPrefix="crm-prospects"
      />
      {/* Phase 75 — tag-as-list filter strip */}
      <div className="flex items-center flex-wrap gap-1.5 mb-3" data-testid="crm-prospects-tag-strip">
        <span className="text-[10px] uppercase tracking-wider text-slate-400 mr-1">Lists:</span>
        <button
          onClick={() => setTagFilter("")}
          className={`text-[11px] px-2 py-0.5 rounded-sm border ${!tagFilter ? "bg-[#F54800] text-white border-[#F54800]" : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"}`}
          data-testid="crm-prospects-tag-all"
        >
          All ({rows.length})
        </button>
        {tagList.map((t) => (
          <button
            key={t.tag}
            onClick={() => setTagFilter(t.tag === tagFilter ? "" : t.tag)}
            className={`text-[11px] px-2 py-0.5 rounded-sm border ${tagFilter === t.tag ? "bg-[#F54800] text-white border-[#F54800]" : "bg-white text-slate-700 border-slate-200 hover:border-slate-300"}`}
            data-testid={`crm-prospects-tag-pill-${t.tag.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
            title={`${t.count} prospect${t.count === 1 ? "" : "s"} tagged "${t.tag}"`}
          >
            {t.tag} <span className="opacity-60 ml-0.5">{t.count}</span>
          </button>
        ))}
        {tagList.length === 0 && (
          <span className="text-[11px] text-slate-300 italic">no tags yet — add tags in the row below to build a list</span>
        )}
      </div>
      <div className="card-flat overflow-hidden">
        {/* Phase 84.3 — Internal scroll: X for wide columns, Y capped so the
            page chrome (header + filters) stays visible while the user
            skims a long list. Sticky <thead> keeps column labels anchored. */}
        <div className="overflow-x-auto overflow-y-auto max-h-[70vh]">
          <table className="text-sm min-w-[1450px] w-full" data-testid="crm-prospects-table">
            <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 sticky top-0 z-10">
              <tr>
                <th className="px-3 py-2 w-8"><input type="checkbox"
                  checked={rows.length > 0 && selected.size === rows.length}
                  onChange={(e) => setSelected(e.target.checked ? new Set(rows.map((r) => r.id)) : new Set())} /></th>
                <th className="px-3 py-2 text-left min-w-[160px]">Name</th>
                <th className="px-3 py-2 text-left min-w-[140px]">Designation</th>
                <th className="px-3 py-2 text-left min-w-[160px]">Company</th>
                <th className="px-3 py-2 text-left min-w-[220px]">Email</th>
                <th className="px-3 py-2 text-left min-w-[130px]">Source</th>
                <th className="px-3 py-2 text-left min-w-[100px]">Type</th>
                <th className="px-3 py-2 text-left min-w-[140px]">Service</th>
                <th className="px-3 py-2 text-left min-w-[160px]">ICP</th>
                <th className="px-3 py-2 text-left min-w-[120px]">Status</th>
                <th className="px-3 py-2 text-left min-w-[220px]">Tags · Lists</th>
                <th className="px-3 py-2 w-24"></th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr><td colSpan={12} className="px-3 py-10 text-center text-slate-400 italic">{tagFilter ? `No prospects in list "${tagFilter}".` : "No prospects yet. Add directly or paste a CSV."}</td></tr>
              )}
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50/60" data-testid={`crm-prospect-row-${r.id}`}>
                  <td className="px-3 py-2"><input type="checkbox" checked={selected.has(r.id)} onChange={(e) => {
                    const s = new Set(selected);
                    if (e.target.checked) s.add(r.id); else s.delete(r.id);
                    setSelected(s);
                  }} /></td>
                  <td className="px-3 py-2"><InlineText value={r.full_name} onSave={(v) => patchField(r.id, "full_name", v)} testId={`crm-prospect-name-${r.id}`} /></td>
                  <td className="px-3 py-2"><InlineText value={r.designation || ""} onSave={(v) => patchField(r.id, "designation", v)} placeholder="—" /></td>
                  <td className="px-3 py-2"><InlineText value={r.company_name || ""} onSave={(v) => patchField(r.id, "company_name", v)} placeholder="—" /></td>
                  <td className="px-3 py-2"><InlineText value={r.email || ""} onSave={(v) => patchField(r.id, "email", v)} placeholder="—" type="email" /></td>
                  <td className="px-3 py-2"><InlineSelect value={r.source || ""} options={["Website", "Referral", "Cold Outreach", "LinkedIn", "Conference", "Partner", "Inbound Call"]} onSave={(v) => patchField(r.id, "source", v)} /></td>
                  <td className="px-3 py-2"><InlineSelect value={r.lead_type || ""} options={LEAD_TYPES} onSave={(v) => patchField(r.id, "lead_type", v)} /></td>
                  <td className="px-3 py-2"><InlineSelect value={r.service || ""} options={SERVICES} onSave={(v) => patchField(r.id, "service", v)} /></td>
                  <td className="px-3 py-2">
                    <select value={r.icp || ""} onChange={(e) => patchField(r.id, "icp", e.target.value || null)} className={`bg-transparent border-0 w-full text-sm py-1 pl-0 pr-6 outline-none ${r.icp === "Student" ? "text-rose-600 font-medium" : ""}`} data-testid={`crm-prospect-icp-${r.id}`}>
                      <option value="">—</option>
                      {ICP_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </td>
                  <td className="px-3 py-2"><InlineSelect value={r.status || ""} options={PROSPECT_STATUSES} onSave={(v) => patchField(r.id, "status", v)} /></td>
                  <td className="px-3 py-2 min-w-[200px]">
                    <TagEditor
                      value={r.tags || []}
                      suggestions={tagList.map((t) => t.tag)}
                      disabled={!canEdit}
                      onChange={(arr) => patchField(r.id, "tags", arr)}
                      testIdPrefix={`crm-prospect-tags-${r.id}`}
                    />
                  </td>
                  <td className="px-3 py-2 text-right">
                    {canEdit && (
                      <div className="inline-flex items-center gap-1 opacity-60 hover:opacity-100">
                        <button onClick={() => convert(r.id)} className="text-[10px] text-[#F54800] hover:underline inline-flex items-center gap-0.5" title="Convert to Lead" data-testid={`crm-prospect-convert-${r.id}`}>
                          Convert <ArrowRight size={10} />
                        </button>
                        <button onClick={() => removeRow(r.id)} className="text-slate-400 hover:text-red-600 p-1" data-testid={`crm-prospect-delete-${r.id}`}>
                          <Trash2 size={11} />
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {bulkOpen && (
        <BulkUploadModal
          kind="prospects"
          endpoint="/crm/prospects/bulk-upload"
          columns={["full_name", "email", "phone", "designation", "company_name", "country", "industry", "linkedin", "icp_match_score", "status", "notes"]}
          requiredCols={["full_name"]}
          onClose={() => setBulkOpen(false)}
          onDone={() => { setBulkOpen(false); load(); }}
        />
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Leads — Kanban (Phase 70 + 73 + 74)                                        */
/* -------------------------------------------------------------------------- */
// Phase 73: trimmed Negotiation + Won; added "Follow-up". "Proposal Sent" is
// now a *migration trigger* — dropping a card here mints a Deal and soft-deletes
// the lead, so the column will always look near-empty in steady state.
// Phase 74: extended schema — Lead Type, Service, ICP, Lead Status, Drive Links,
// Lead Value (₹), human-readable Lead ID code, computed Lead Age.
// Phase 75: stages / formatters / primitives moved to ./crm/_shared.

function LeadsKanbanTab() {
  const { user } = useAuth();
  const can = usePermissions();
  const canEdit = user?.role !== "client" && user?.role !== "vendor";
  const isAdmin = can("crm_admin");
  // Feb 2026 · runtime stage vocabulary. Admins can rename any stage inline
  // (except the reserved `Proposal Sent` migration trigger). The hook is
  // shared across every CRM component so a rename here immediately updates
  // the drawer + funnel + settings tab without a page reload.
  const { stages, reserved, rename: renameStage } = useLeadStages();
  const [leads, setLeads] = useState(null);
  const [search, setSearch] = useState("");
  const [creating, setCreating] = useState(false);
  const [dragOver, setDragOver] = useState(null); // stage being hovered
  const [detail, setDetail] = useState(null); // lead currently open in the drawer

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/crm/pipeline/leads", { params: { q: search || undefined } });
      setLeads(data || []);
    } catch (e) { toast.error(formatApiError(e)); }
  }, [search]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const addLead = async () => {
    setCreating(true);
    try {
      // Feb 2026 · use the runtime "first stage" so renaming "New Inquiry"
      // doesn't break the New-lead action. Backend also defaults to
      // stages_now[0] if the client sends nothing.
      const firstStage = stages?.[0] || "New Inquiry";
      const { data } = await api.post("/crm/pipeline/leads", { company_name: "New lead", stage: firstStage });
      setLeads((r) => [data, ...(r || [])]);
      toast.success("Lead added");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setCreating(false); }
  };

  // Drag-and-drop handlers (native HTML5)
  const onDragStart = (e, leadId) => {
    e.dataTransfer.setData("text/plain", leadId);
    e.dataTransfer.effectAllowed = "move";
  };

  const onDragOver = (e, stage) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (dragOver !== stage) setDragOver(stage);
  };

  const onDragLeave = (e) => {
    // Only clear if we leave the column entirely
    if (e.currentTarget && !e.currentTarget.contains(e.relatedTarget)) {
      setDragOver(null);
    }
  };

  const onDrop = async (e, newStage) => {
    e.preventDefault();
    setDragOver(null);
    const leadId = e.dataTransfer.getData("text/plain");
    if (!leadId) return;
    const lead = (leads || []).find((l) => l.id === leadId);
    if (!lead || lead.stage === newStage) return;

    // Optimistic UI — Proposal Sent triggers migration so we also drop it
    // from the local list immediately; backend will confirm with the deal id.
    if (newStage === "Proposal Sent") {
      setLeads((r) => r.filter((l) => l.id !== leadId));
    } else {
      setLeads((r) => r.map((l) => l.id === leadId ? { ...l, stage: newStage } : l));
    }
    try {
      const { data } = await api.patch(`/crm/pipeline/leads/${leadId}`, { stage: newStage });
      if (data?._migrated_to_deal_id) {
        toast.success("Lead promoted to Deal (Proposal Sent) — see the Deals tab.");
      } else {
        toast.success(`Moved to ${newStage}`);
      }
    } catch (err) {
      toast.error(formatApiError(err));
      load(); // rollback
    }
  };

  const removeLead = async (id) => {
    if (!window.confirm("Delete this lead?")) return;
    setLeads((r) => r.filter((x) => x.id !== id));
    try { await api.delete(`/crm/pipeline/leads/${id}`); }
    catch (e) { toast.error(formatApiError(e)); load(); }
  };

  const patchField = async (id, field, value) => {
    setLeads((r) => r.map((x) => x.id === id ? { ...x, [field]: value } : x));
    try {
      const { data } = await api.patch(`/crm/pipeline/leads/${id}`, { [field]: value });
      if (data?._migrated_to_deal_id) {
        // Lead got migrated — drop it from the local list and notify.
        setLeads((r) => r.filter((x) => x.id !== id));
        toast.success("Lead promoted to Deal (Proposal Sent) — see the Deals tab.");
      }
    } catch (e) { toast.error(formatApiError(e)); load(); }
  };

  if (!leads) return <Loading />;

  // Group leads by stage (runtime stage list)
  const byStage = Object.fromEntries(stages.map((s) => [s, []]));
  for (const l of leads) {
    const s = byStage[l.stage] ? l.stage : (stages[0] || "New Inquiry");
    byStage[s].push(l);
  }

  return (
    <div data-testid="crm-leads-kanban-tab">
      {/* Toolbar */}
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by company, contact, email…"
            className="pl-7 rounded-sm h-9 text-sm"
            data-testid="crm-leads-search"
          />
        </div>
        <div className="text-xs text-slate-500">{leads.length} lead{leads.length === 1 ? "" : "s"}</div>
        {canEdit && (
          <Button size="sm" onClick={addLead} disabled={creating} className="rounded-sm h-9 bg-[#F54800] hover:bg-[#D63E00] text-white ml-auto" data-testid="crm-leads-add">
            {creating ? <Loader2 size={12} className="mr-1.5 animate-spin" /> : <Plus size={12} className="mr-1.5" />} New lead
          </Button>
        )}
      </div>

      {/* Feb 2026 · rename hint for admins so the feature is discoverable
          without hovering. Sits above the board like a helper caption. */}
      {isAdmin && (
        <div className="text-[11px] text-slate-500 mb-2 flex items-center gap-1.5" data-testid="crm-leads-rename-hint">
          <Pencil size={10} className="text-slate-400" />
          Column names are editable — click any column header to rename it. Existing leads move automatically.
        </div>
      )}
      {/* Kanban board */}
      <div className="grid grid-flow-col auto-cols-[280px] gap-3 overflow-x-auto pb-4" data-testid="crm-leads-board">
        {stages.map((stage, idx) => {
          const tone = stageToneFor(stage, idx);
          const cards = byStage[stage] || [];
          const isOver = dragOver === stage;
          const isReserved = (reserved || []).includes(stage);
          return (
            <div
              key={stage}
              onDragOver={(e) => canEdit && onDragOver(e, stage)}
              onDragLeave={onDragLeave}
              onDrop={(e) => canEdit && onDrop(e, stage)}
              className={`flex flex-col rounded-sm border transition-colors ${isOver ? "border-[#F54800] bg-[#F54800]/5" : "border-slate-200 bg-slate-50/40"}`}
              data-testid={`crm-lead-col-${stage.replace(/\s+/g, "-").toLowerCase()}`}
            >
              <div className="flex items-center justify-between px-3 py-2 border-b border-slate-200 bg-white rounded-t-sm">
                <div className={`inline-flex items-center gap-2 text-xs font-medium ${tone.header} flex-1 min-w-0`}>
                  <span className={`w-2 h-2 rounded-full shrink-0 ${tone.dot}`} />
                  {/* Feb 2026 · click to rename (admin only, non-reserved stages) */}
                  <StageHeaderName
                    stage={stage}
                    canRename={isAdmin && !isReserved}
                    isReserved={isReserved}
                    onRename={async (newLabel) => {
                      try {
                        const res = await renameStage(stage, newLabel);
                        toast.success(
                          res.no_change ? "No change" :
                          `Renamed to "${newLabel}"${res.renamed ? ` · migrated ${res.renamed} lead${res.renamed === 1 ? "" : "s"}` : ""}`
                        );
                        // Refetch leads so cards show the new stage label
                        // in their pipeline-selector dropdown.
                        load();
                      } catch (e) {
                        toast.error(formatApiError(e));
                      }
                    }}
                  />
                </div>
                <span className="text-[10px] text-slate-400 font-medium shrink-0 ml-1">{cards.length}</span>
              </div>
              <div className="flex-1 min-h-[120px] p-2 space-y-2">
                {cards.length === 0 && (
                  <div className="text-[11px] text-slate-300 italic text-center py-6 select-none">Drop here</div>
                )}
                {cards.map((l) => (
                  <LeadCard
                    key={l.id}
                    lead={l}
                    canEdit={canEdit}
                    onDragStart={onDragStart}
                    onDelete={removeLead}
                    onPatch={patchField}
                    onOpen={setDetail}
                  />
                ))}
              </div>
            </div>
          );
        })}
      </div>
      {detail && (
        <LeadDetailDrawer
          lead={detail}
          canEdit={canEdit}
          onClose={() => setDetail(null)}
          onPatch={async (field, value) => {
            await patchField(detail.id, field, value);
            // Sync the open drawer with the local edit so the UI doesn't flash stale data.
            setDetail((d) => d ? { ...d, [field]: value } : d);
          }}
        />
      )}
    </div>
  );
}

// LeadCard + LeadDetailDrawer extracted to /pages/crm/ — see imports above.

/* -------------------------------------------------------------------------- */
/*  Feb 2026 · Inline stage rename (admin only)                                */
/* -------------------------------------------------------------------------- */
/** Header text on each Kanban column. Read-only for non-admins. Admins get:
 *  - always-visible pencil icon on the right of the column header (subtle)
 *  - hover on header row highlights the pencil + name to signal "editable"
 *  - clicking either the name OR the pencil enters rename mode
 *  - Enter / blur commits, Escape reverts
 *  Reserved stages (Proposal Sent) show a small lock icon + tooltip. */
function StageHeaderName({ stage, canRename, isReserved, onRename }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(stage);
  const inputRef = React.useRef(null);
  useEffect(() => { setDraft(stage); }, [stage]);
  useEffect(() => { if (editing) setTimeout(() => inputRef.current?.select(), 20); }, [editing]);

  const testIdSlug = stage.replace(/\s+/g, "-").toLowerCase();

  if (editing) {
    return (
      <input
        ref={inputRef}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => {
          setEditing(false);
          const next = draft.trim();
          if (next && next !== stage) onRename(next);
          else setDraft(stage);
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter") { e.preventDefault(); e.currentTarget.blur(); }
          if (e.key === "Escape") { setDraft(stage); setEditing(false); }
        }}
        maxLength={40}
        className="bg-white border border-[#F54800] rounded-sm px-1.5 py-0.5 text-xs font-medium outline-none focus:ring-1 focus:ring-[#F54800] w-full"
        data-testid={`crm-stage-rename-input-${testIdSlug}`}
      />
    );
  }
  if (isReserved) {
    return (
      <span
        className="inline-flex items-center gap-1 truncate"
        title="This stage is reserved — it drives the Deal migration flow and can't be renamed."
        data-testid={`crm-stage-name-${testIdSlug}`}
      >
        {stage}
        <Lock size={9} className="text-slate-400 shrink-0" aria-label="reserved" />
      </span>
    );
  }
  if (!canRename) {
    return (
      <span className="truncate" data-testid={`crm-stage-name-${testIdSlug}`}>{stage}</span>
    );
  }
  return (
    <button
      type="button"
      onClick={() => setEditing(true)}
      className="group/rename inline-flex items-center gap-1.5 truncate min-w-0 hover:text-[#F54800] transition-colors cursor-pointer focus:outline-none"
      title="Click to rename this stage — existing leads will move to the new name automatically."
      data-testid={`crm-stage-name-${testIdSlug}`}
    >
      <span className="truncate">{stage}</span>
      <Pencil
        size={10}
        className="text-slate-400 group-hover/rename:text-[#F54800] shrink-0 opacity-70 group-hover/rename:opacity-100 transition-all"
        aria-label="Rename stage"
      />
    </button>
  );
}



/* -------------------------------------------------------------------------- */
/*  Deals tab (Phase 71)                                                       */
/* -------------------------------------------------------------------------- */
const DEAL_STAGES = [
  "Proposal Drafting", "Proposal Sent", "Negotiating",
  "MSA Drafting", "MSA Sent", "MSA Signed", "Won", "Lost",
];
const MSA_STATUSES = ["Not Started", "Drafting", "Sent for Review", "Signed", "Cancelled"];

const DEAL_STAGE_TONE = {
  "Proposal Drafting": "bg-slate-100 text-slate-700",
  "Proposal Sent":     "bg-blue-100 text-blue-700",
  "Negotiating":       "bg-amber-100 text-amber-700",
  "MSA Drafting":      "bg-purple-100 text-purple-700",
  "MSA Sent":          "bg-indigo-100 text-indigo-700",
  "MSA Signed":        "bg-teal-100 text-teal-700",
  "Won":               "bg-emerald-100 text-emerald-700",
  "Lost":              "bg-rose-100 text-rose-700",
};

function DealsTab() {
  const { user } = useAuth();
  const canEdit = user?.role !== "client" && user?.role !== "vendor";
  const [rows, setRows] = useState(null);
  const [search, setSearch] = useState("");
  const [stageFilter, setStageFilter] = useState("");
  const [selected, setSelected] = useState(() => new Set());
  const [creating, setCreating] = useState(false);
  const [lostFor, setLostFor] = useState(null);          // deal id awaiting lost reason
  const [lostReason, setLostReason] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/crm/deals", { params: { q: search || undefined, stage: stageFilter || undefined } });
      setRows(data || []);
    } catch (e) { toast.error(formatApiError(e)); }
  }, [search, stageFilter]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const addRow = async () => {
    setCreating(true);
    try {
      const { data } = await api.post("/crm/deals", { company_name: "New deal", stage: "Proposal Drafting" });
      setRows((r) => [data, ...(r || [])]);
      toast.success("Deal added");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setCreating(false); }
  };

  const patchField = async (id, field, value) => {
    setRows((r) => r.map((x) => x.id === id ? { ...x, [field]: value } : x));
    try { await api.patch(`/crm/deals/${id}`, { [field]: value }); load(); }
    catch (e) { toast.error(formatApiError(e)); load(); }
  };

  const removeRow = async (id) => {
    if (!window.confirm("Delete this deal? (Soft-delete — recoverable via audit log.)")) return;
    setRows((r) => r.filter((x) => x.id !== id));
    try { await api.delete(`/crm/deals/${id}`); }
    catch (e) { toast.error(formatApiError(e)); load(); }
  };

  const bulkDelete = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`Delete ${selected.size} deals?`)) return;
    try {
      await api.post("/crm/deals/bulk-delete", { ids: Array.from(selected) });
      toast.success(`${selected.size} deleted`);
      setSelected(new Set());
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const markWon = async (id) => {
    try {
      await api.post(`/crm/deals/${id}/mark-won`);
      toast.success("Deal marked as Won 🎉");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const submitLost = async () => {
    if (!lostFor) return;
    try {
      await api.post(`/crm/deals/${lostFor}/mark-lost`, { lost_reason: lostReason || null });
      toast.success("Deal marked as Lost");
      setLostFor(null); setLostReason("");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!rows) return <Loading />;

  return (
    <div data-testid="crm-deals-tab">
      {/* Toolbar */}
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by company, contact, notes…"
            className="pl-7 rounded-sm h-9 text-sm"
            data-testid="crm-deals-search"
          />
        </div>
        <select value={stageFilter} onChange={(e) => setStageFilter(e.target.value)} className="rounded-sm border border-slate-200 text-sm h-9 px-2" data-testid="crm-deals-stage-filter">
          <option value="">All stages</option>
          {DEAL_STAGES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        {selected.size > 0 && (
          <Button variant="outline" size="sm" onClick={bulkDelete} className="rounded-sm h-9 border-red-200 text-red-700 hover:bg-red-50" data-testid="crm-deals-bulk-delete">
            <Trash2 size={12} className="mr-1.5" /> Delete {selected.size}
          </Button>
        )}
        {canEdit && (
          <Button size="sm" onClick={addRow} disabled={creating} className="rounded-sm h-9 bg-[#F54800] hover:bg-[#D63E00] text-white" data-testid="crm-deals-add">
            {creating ? <Loader2 size={12} className="mr-1.5 animate-spin" /> : <Plus size={12} className="mr-1.5" />} New deal
          </Button>
        )}
      </div>

      {/* Table */}
      <div className="card-flat overflow-hidden">
        {/* Phase 84.3 — Internal X + Y scroll, sticky header. */}
        <div className="overflow-x-auto overflow-y-auto max-h-[70vh]">
          <table className="text-sm min-w-[1400px] w-full" data-testid="crm-deals-table">
            <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 sticky top-0 z-10">
              <tr>
                <th className="px-3 py-2 w-8"><input type="checkbox"
                  checked={rows.length > 0 && selected.size === rows.length}
                  onChange={(e) => setSelected(e.target.checked ? new Set(rows.map((r) => r.id)) : new Set())} /></th>
                <th className="px-3 py-2 text-left min-w-[180px]">Company</th>
                <th className="px-3 py-2 text-left min-w-[160px]">Contact</th>
                <th className="px-3 py-2 text-left min-w-[140px]">Stage</th>
                <th className="px-3 py-2 text-right min-w-[130px]">Proposal ₹</th>
                <th className="px-3 py-2 text-left min-w-[70px]">Ver</th>
                <th className="px-3 py-2 text-left min-w-[220px]">Proposal Link</th>
                <th className="px-3 py-2 text-left min-w-[140px]">MSA Status</th>
                <th className="px-3 py-2 text-left min-w-[130px]">MSA Signed</th>
                <th className="px-3 py-2 text-left min-w-[130px]">Close Date</th>
                <th className="px-3 py-2 w-24"></th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr><td colSpan={11} className="px-3 py-10 text-center text-slate-400 italic">
                  No deals yet. They&apos;ll appear here automatically when a Lead lands in &quot;Proposal Requested&quot;, or click &quot;New deal&quot; to add one manually.
                </td></tr>
              )}
              {rows.map((r) => {
                const isTerminal = r.stage === "Won" || r.stage === "Lost";
                return (
                  <tr key={r.id} className={`border-t border-slate-100 hover:bg-slate-50/60 ${isTerminal ? "opacity-70" : ""}`} data-testid={`crm-deal-row-${r.id}`}>
                    <td className="px-3 py-2"><input type="checkbox" checked={selected.has(r.id)} onChange={(e) => {
                      const s = new Set(selected);
                      if (e.target.checked) s.add(r.id); else s.delete(r.id);
                      setSelected(s);
                    }} /></td>
                    <td className="px-3 py-2 min-w-[150px]">
                      <InlineText value={r.company_name || ""} onSave={(v) => patchField(r.id, "company_name", v)} testId={`crm-deal-company-${r.id}`} placeholder="Company" />
                    </td>
                    <td className="px-3 py-2 min-w-[120px]">
                      <InlineText value={r.contact_name || ""} onSave={(v) => patchField(r.id, "contact_name", v)} placeholder="—" />
                    </td>
                    <td className="px-3 py-2 min-w-[140px]">
                      <select
                        value={r.stage || ""}
                        onChange={(e) => patchField(r.id, "stage", e.target.value)}
                        className={`text-[11px] font-medium rounded-sm px-2 py-1 border-0 cursor-pointer ${DEAL_STAGE_TONE[r.stage] || "bg-slate-100 text-slate-700"}`}
                        data-testid={`crm-deal-stage-${r.id}`}
                      >
                        {DEAL_STAGES.map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </td>
                    <td className="px-3 py-2 min-w-[100px] text-right">
                      <InlineText value={r.proposal_value ?? ""} onSave={(v) => patchField(r.id, "proposal_value", v ? Number(v) : null)} placeholder="—" type="number" testId={`crm-deal-proposal-value-${r.id}`} />
                    </td>
                    <td className="px-3 py-2 min-w-[50px]">
                      <InlineText value={r.proposal_version ?? 1} onSave={(v) => patchField(r.id, "proposal_version", v ? Number(v) : 1)} type="number" />
                    </td>
                    <td className="px-3 py-2 min-w-[120px]">
                      {r.proposal_doc_link ? (
                        <a href={r.proposal_doc_link} target="_blank" rel="noopener noreferrer" className="text-[11px] text-blue-600 hover:underline truncate inline-block max-w-[140px]" data-testid={`crm-deal-proposal-link-${r.id}`}>
                          {r.proposal_doc_link.replace(/^https?:\/\//, "").slice(0, 22)}…
                        </a>
                      ) : (
                        <InlineText value={r.proposal_doc_link || ""} onSave={(v) => patchField(r.id, "proposal_doc_link", v || null)} placeholder="Paste link" />
                      )}
                    </td>
                    <td className="px-3 py-2 min-w-[120px]">
                      <select
                        value={r.msa_status || "Not Started"}
                        onChange={(e) => patchField(r.id, "msa_status", e.target.value)}
                        className="text-[11px] rounded-sm border border-slate-200 px-1.5 py-0.5 cursor-pointer bg-white"
                        data-testid={`crm-deal-msa-status-${r.id}`}
                      >
                        {MSA_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </td>
                    <td className="px-3 py-2 min-w-[110px]">
                      <InlineText value={r.msa_signed_date || ""} onSave={(v) => patchField(r.id, "msa_signed_date", v || null)} placeholder="YYYY-MM-DD" />
                    </td>
                    <td className="px-3 py-2 min-w-[110px]">
                      <InlineText value={r.expected_close_date || ""} onSave={(v) => patchField(r.id, "expected_close_date", v || null)} placeholder="YYYY-MM-DD" />
                    </td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      {/* Phase 84.8 — quick chip showing contract activity on this deal */}
                      {(r.contracts_timeline || []).length > 0 && (
                        <a href="/crm?tab=contracts" className="inline-block mr-2 text-[10px] text-purple-700 bg-purple-50 hover:bg-purple-100 px-1.5 py-0.5 rounded-sm" title={`${(r.contracts_timeline || []).length} contract event(s)`} data-testid={`crm-deal-contracts-chip-${r.id}`}>
                          {(r.contracts_timeline || []).length} contract{(r.contracts_timeline || []).length > 1 ? "s" : ""}
                        </a>
                      )}
                      {canEdit && !isTerminal && (
                        <div className="inline-flex items-center gap-1.5">
                          <button onClick={() => markWon(r.id)} className="text-[10px] text-emerald-700 hover:bg-emerald-50 px-1.5 py-0.5 rounded-sm font-medium" title="Mark as Won" data-testid={`crm-deal-mark-won-${r.id}`}>
                            Won
                          </button>
                          <button onClick={() => { setLostFor(r.id); setLostReason(""); }} className="text-[10px] text-rose-700 hover:bg-rose-50 px-1.5 py-0.5 rounded-sm font-medium" title="Mark as Lost" data-testid={`crm-deal-mark-lost-${r.id}`}>
                            Lost
                          </button>
                          <button onClick={() => removeRow(r.id)} className="text-slate-400 hover:text-red-600 p-1" data-testid={`crm-deal-delete-${r.id}`}>
                            <Trash2 size={11} />
                          </button>
                        </div>
                      )}
                      {isTerminal && r.won_date && <span className="text-[10px] text-emerald-700">won {r.won_date}</span>}
                      {isTerminal && r.stage === "Lost" && r.lost_reason && (
                        <span className="text-[10px] text-rose-700 italic" title={r.lost_reason}>{r.lost_reason.slice(0, 24)}{r.lost_reason.length > 24 ? "…" : ""}</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Lost-reason modal */}
      {lostFor && (
        <div
          className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
          onClick={() => setLostFor(null)}
          data-testid="crm-deal-lost-modal"
        >
          <div className="bg-white rounded-sm p-5 max-w-sm w-full shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="text-sm font-semibold text-slate-900 mb-1">Mark deal as Lost</div>
            <div className="text-xs text-slate-500 mb-3">Optional — capture the reason for analytics.</div>
            <textarea
              autoFocus
              value={lostReason}
              onChange={(e) => setLostReason(e.target.value)}
              placeholder="e.g. Lost on price · went with competitor · no budget…"
              rows={3}
              className="w-full rounded-sm border border-slate-200 px-2 py-1.5 text-sm focus:ring-1 focus:ring-[#F54800] focus:border-[#F54800] outline-none"
              data-testid="crm-deal-lost-reason-input"
            />
            <div className="flex justify-end gap-2 mt-3">
              <Button variant="outline" size="sm" onClick={() => setLostFor(null)} className="rounded-sm h-8">Cancel</Button>
              <Button size="sm" onClick={submitLost} className="rounded-sm h-8 bg-rose-600 hover:bg-rose-700 text-white" data-testid="crm-deal-lost-confirm">
                Mark as Lost
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}



/* -------------------------------------------------------------------------- */
/*  Clients tab (Phase 84.4 — retired)                                         */
/*                                                                             */
/*  The legacy sales-snapshot ClientsTab has been replaced by <AdminClients    */
/*  embedded /> (the full Studio client directory). Keeping the tag comment    */
/*  so the rendering site above documents the move.                            */
/* -------------------------------------------------------------------------- */


/* -------------------------------------------------------------------------- */
/*  Testimonials tab (Phase 139) — client-submitted quotes, landed here for   */
/*  Founders/BD to browse and lift for marketing use.                        */
/* -------------------------------------------------------------------------- */
function TestimonialsTab() {
  const [rows, setRows] = useState(null);

  useEffect(() => {
    api.get("/testimonials").then(({ data }) => setRows(data || [])).catch((e) => { toast.error(formatApiError(e)); setRows([]); });
  }, []);

  if (!rows) return <Loading />;

  return (
    <div data-testid="crm-testimonials-tab">
      <div className="text-xs text-slate-500 mb-3">Testimonials clients share from their portal — either the project-wrap prompt or the always-open &quot;Share a testimonial&quot; field on their Home page.</div>
      <div className="space-y-3">
        {rows.length === 0 && (
          <div className="card-flat p-8 text-center text-slate-400 italic" data-testid="crm-testimonials-empty">No testimonials submitted yet.</div>
        )}
        {rows.map((t) => (
          <div key={t.id} className="card-flat p-5" data-testid={`crm-testimonial-${t.id}`}>
            <div className="flex items-center justify-between gap-3 flex-wrap mb-2">
              <div>
                <div className="text-sm font-semibold text-slate-900">{t.user_name || t.client_email}</div>
                <div className="text-[11px] text-slate-500">{t.project_name} · {t.created_at ? new Date(t.created_at).toLocaleDateString() : ""}</div>
              </div>
              <div className="flex items-center gap-1 text-base text-[#F54800]">
                {"★".repeat(t.rating || 0)}<span className="text-slate-200">{"★".repeat(Math.max(0, 5 - (t.rating || 0)))}</span>
              </div>
            </div>
            <p className="text-sm text-slate-700 italic">&quot;{t.testimonial}&quot;</p>
            {t.google_review_consent && (
              <span className="inline-block mt-2 text-[10px] uppercase tracking-wider text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-sm">OK to post as Google review</span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Reports tab (Phase 72)                                                     */
/* -------------------------------------------------------------------------- */
function ReportsTab() {
  const [funnel, setFunnel] = useState(null);
  const [conversion, setConversion] = useState(null);
  const [velocity, setVelocity] = useState(null);
  const [scorecard, setScorecard] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get("/crm/reports/funnel").then(({ data }) => setFunnel(data)).catch(() => setFunnel(null)),
      api.get("/crm/reports/conversion").then(({ data }) => setConversion(data)).catch(() => setConversion(null)),
      api.get("/crm/reports/velocity").then(({ data }) => setVelocity(data)).catch(() => setVelocity(null)),
      api.get("/crm/reports/owner-scorecard").then(({ data }) => setScorecard(data)).catch(() => setScorecard(null)),
    ]);
  }, []);

  const fmt = inr;

  return (
    <div className="space-y-6" data-testid="crm-reports-tab">
      {/* Funnel chart */}
      {funnel ? <FunnelChart funnel={funnel} /> : <Loading />}

      {/* Conversion rates table */}
      <div className="card-flat overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-200">
          <div className="font-display text-sm font-semibold text-slate-900">Step-by-step conversion</div>
          <div className="text-xs text-slate-500">Percentage of upstream that advanced to the next funnel step</div>
        </div>
        {!conversion ? <div className="p-5"><Loading /></div> : (
          <table className="w-full text-sm" data-testid="crm-reports-conversion-table">
            <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-4 py-2 text-left">From</th>
                <th className="px-4 py-2 text-right">From count</th>
                <th className="px-4 py-2 text-left">To</th>
                <th className="px-4 py-2 text-right">To count</th>
                <th className="px-4 py-2 text-right">Conversion</th>
              </tr>
            </thead>
            <tbody>
              {(conversion.rows || []).map((r, i) => (
                <tr key={i} className="border-t border-slate-100">
                  <td className="px-4 py-2 text-slate-700">{r.from}</td>
                  <td className="px-4 py-2 text-right text-slate-700">{r.from_count}</td>
                  <td className="px-4 py-2 text-slate-700">{r.to}</td>
                  <td className="px-4 py-2 text-right text-slate-700">{r.to_count}</td>
                  <td className="px-4 py-2 text-right">
                    <span className={`text-[11px] font-medium px-1.5 py-0.5 rounded-sm ${r.conversion >= 30 ? "bg-emerald-100 text-emerald-700" : r.conversion >= 10 ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-600"}`}>
                      {r.conversion > 999 ? "999+" : r.conversion}%
                    </span>
                  </td>
                </tr>
              ))}
              {(conversion.rows || []).length === 0 && (
                <tr><td colSpan={5} className="px-4 py-6 text-center text-slate-400 italic">No data yet — start by adding Target Accounts.</td></tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* Velocity */}
      {velocity && (
        <div>
          <div className="font-display text-sm font-semibold text-slate-900 mb-2">Sales velocity</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="crm-reports-velocity">
            <VelocityCard label="Proposal Sent → Won" value={velocity.avg_days_proposal_sent_to_won} testId="crm-reports-velocity-proposal-to-won" />
            <VelocityCard label="Deal Created → Won" value={velocity.avg_days_deal_created_to_won} testId="crm-reports-velocity-deal-to-won" />
            <VelocityCard label="MSA Signed → Won"   value={velocity.avg_days_msa_signed_to_won}    testId="crm-reports-velocity-msa-to-won" />
          </div>
          <div className="text-[10px] text-slate-400 mt-1">Based on {velocity.won_deal_count || 0} won deal{velocity.won_deal_count === 1 ? "" : "s"}.</div>
        </div>
      )}

      {/* Owner scorecard */}
      <div className="card-flat overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-200">
          <div className="font-display text-sm font-semibold text-slate-900">Owner scorecard</div>
          <div className="text-xs text-slate-500">Per-BD-owner pipeline performance · sorted by won value</div>
        </div>
        {!scorecard ? <div className="p-5"><Loading /></div> : (
          <table className="w-full text-sm" data-testid="crm-reports-scorecard-table">
            <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-4 py-2 text-left">Owner</th>
                <th className="px-4 py-2 text-right">Leads</th>
                <th className="px-4 py-2 text-right">Deals</th>
                <th className="px-4 py-2 text-right">Won</th>
                <th className="px-4 py-2 text-right">Lost</th>
                <th className="px-4 py-2 text-right">Close rate</th>
                <th className="px-4 py-2 text-right">Pipeline ₹</th>
                <th className="px-4 py-2 text-right">Won ₹</th>
              </tr>
            </thead>
            <tbody>
              {(scorecard.rows || []).map((r) => (
                <tr key={r.owner_id} className="border-t border-slate-100 hover:bg-slate-50/60" data-testid={`crm-scorecard-row-${r.owner_id}`}>
                  <td className="px-4 py-2 font-medium text-slate-900">{r.owner_name}</td>
                  <td className="px-4 py-2 text-right text-slate-700">{r.leads}</td>
                  <td className="px-4 py-2 text-right text-slate-700">{r.deals}</td>
                  <td className="px-4 py-2 text-right text-emerald-700 font-medium">{r.deals_won}</td>
                  <td className="px-4 py-2 text-right text-rose-700">{r.deals_lost}</td>
                  <td className="px-4 py-2 text-right">
                    <span className={`text-[11px] font-medium px-1.5 py-0.5 rounded-sm ${r.close_rate >= 30 ? "bg-emerald-100 text-emerald-700" : r.close_rate >= 10 ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-600"}`}>
                      {r.close_rate}%
                    </span>
                  </td>
                  <td className="px-4 py-2 text-right text-slate-700">{fmt(r.pipeline_value || 0)}</td>
                  <td className="px-4 py-2 text-right text-emerald-700 font-bold">{fmt(r.won_value || 0)}</td>
                </tr>
              ))}
              {(scorecard.rows || []).length === 0 && (
                <tr><td colSpan={8} className="px-4 py-6 text-center text-slate-400 italic">No owner activity yet.</td></tr>
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}


/* -------------------------------------------------------------------------- */
/*  Reusable bits                                                              */
/* -------------------------------------------------------------------------- */

function Toolbar({ search, onSearch, statusOptions, status, onStatus, onAdd, adding, selectedCount, onBulkDelete, onBulkUpload, testIdPrefix }) {
  return (
    <div className="flex items-center gap-2 mb-3 flex-wrap">
      <div className="relative flex-1 min-w-[200px] max-w-md">
        <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input
          value={search}
          onChange={(e) => onSearch(e.target.value)}
          placeholder="Search by name, company, country…"
          className="pl-7 rounded-sm h-9 text-sm"
          data-testid={`${testIdPrefix}-search`}
        />
      </div>
      <select value={status} onChange={(e) => onStatus(e.target.value)} className="rounded-sm border border-slate-200 text-sm h-9 px-2" data-testid={`${testIdPrefix}-status-filter`}>
        <option value="">All statuses</option>
        {statusOptions.map((s) => <option key={s} value={s}>{s}</option>)}
      </select>
      {selectedCount > 0 && (
        <Button variant="outline" size="sm" onClick={onBulkDelete} className="rounded-sm h-9 border-red-200 text-red-700 hover:bg-red-50" data-testid={`${testIdPrefix}-bulk-delete`}>
          <Trash2 size={12} className="mr-1.5" /> Delete {selectedCount}
        </Button>
      )}
      {onBulkUpload && (
        <Button variant="outline" size="sm" onClick={onBulkUpload} className="rounded-sm h-9" data-testid={`${testIdPrefix}-bulk-upload`}>
          <Upload size={12} className="mr-1.5" /> Bulk upload
        </Button>
      )}
      {onAdd && (
        <Button size="sm" onClick={onAdd} disabled={adding} className="rounded-sm h-9 bg-[#F54800] hover:bg-[#D63E00] text-white" data-testid={`${testIdPrefix}-add`}>
          {adding ? <Loader2 size={12} className="mr-1.5 animate-spin" /> : <Plus size={12} className="mr-1.5" />} Add
        </Button>
      )}
    </div>
  );
}

// Phase 75: InlineText moved to ./crm/_shared (imported above).

function InlineSelect({ value, options, onSave }) {
  return (
    <select
      value={value}
      onChange={(e) => onSave(e.target.value)}
      // Phase 84.3 — right-padded so the built-in caret doesn't overlap the
      // text (previously showed "Website↓" clipped as "Websitev" in narrow cells).
      className="bg-transparent border-0 w-full text-sm py-1 pl-0 pr-6 focus:bg-white focus:ring-1 focus:ring-[#F54800] focus:pl-1.5 focus:rounded-sm transition-all cursor-pointer outline-none"
    >
      <option value="">—</option>
      {options.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  );
}

function TagEditor({ value, suggestions, disabled, onChange, testIdPrefix }) {
  const [draft, setDraft] = useState("");
  const [showSugg, setShowSugg] = useState(false);
  const tags = value || [];
  const addTag = (raw) => {
    const t = (raw || "").trim();
    if (!t || tags.includes(t)) { setDraft(""); return; }
    onChange([...tags, t]);
    setDraft("");
  };
  const removeTag = (t) => onChange(tags.filter((x) => x !== t));
  const filteredSuggestions = (suggestions || []).filter(
    (s) => !tags.includes(s) && (!draft || s.toLowerCase().includes(draft.toLowerCase())),
  ).slice(0, 6);
  return (
    <div className="relative" data-testid={testIdPrefix}>
      <div className="flex flex-wrap items-center gap-1">
        {tags.map((t) => (
          <span key={t} className="inline-flex items-center gap-0.5 text-[10px] bg-[#F54800]/10 text-[#D63E00] pl-1.5 pr-0.5 py-0.5 rounded-sm font-medium" data-testid={`${testIdPrefix}-chip-${t.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}>
            {t}
            {!disabled && (
              <button onClick={() => removeTag(t)} className="opacity-60 hover:opacity-100 p-0.5">
                <X size={9} />
              </button>
            )}
          </span>
        ))}
        {!disabled && (
          <input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === ",") { e.preventDefault(); addTag(draft); }
              else if (e.key === "Backspace" && !draft && tags.length > 0) {
                e.preventDefault();
                removeTag(tags[tags.length - 1]);
              }
            }}
            onFocus={() => setShowSugg(true)}
            onBlur={() => setTimeout(() => setShowSugg(false), 150)}
            placeholder={tags.length === 0 ? "Add tag…" : ""}
            className="bg-transparent border-0 text-[11px] py-0.5 min-w-[60px] flex-1 outline-none focus:ring-0"
            data-testid={`${testIdPrefix}-input`}
          />
        )}
      </div>
      {showSugg && filteredSuggestions.length > 0 && (
        <div className="absolute z-20 left-0 right-0 mt-1 bg-white border border-slate-200 rounded-sm shadow-md max-h-40 overflow-y-auto">
          {filteredSuggestions.map((s) => (
            <button
              key={s}
              onMouseDown={(e) => { e.preventDefault(); addTag(s); }}
              className="block w-full text-left text-[11px] px-2 py-1 hover:bg-slate-50 text-slate-700"
              data-testid={`${testIdPrefix}-suggestion-${s.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
            >
              {s}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}


function ComingSoon({ tab }) {
  return (
    <div className="card-flat p-10 text-center" data-testid={`crm-coming-soon-${tab}`}>
      <div className="font-display text-lg font-bold text-slate-900 capitalize">{tab.replace("-", " ")} — coming soon</div>
      <div className="text-sm text-slate-500 mt-1">Phase 72 ships Clients + Reports + Settings.</div>
    </div>
  );
}

// Phase 75: Loading moved to ./crm/_shared (imported above).

/* -------------------------------------------------------------------------- */
/*  Bulk CSV upload modal (Phase 73)                                           */
/* -------------------------------------------------------------------------- */
// Tiny CSV parser — handles quoted cells, embedded commas in quotes, and
// CRLF newlines. We avoid an npm dep because the inputs are tame (10-1000 rows
// pasted from Sheets/Excel/Notion).
function parseCSV(text) {
  const rows = [];
  let i = 0, field = "", row = [], inQuotes = false;
  while (i < text.length) {
    const c = text[i];
    if (inQuotes) {
      if (c === '"') {
        if (text[i + 1] === '"') { field += '"'; i += 2; continue; }
        inQuotes = false; i++; continue;
      }
      field += c; i++; continue;
    }
    if (c === '"') { inQuotes = true; i++; continue; }
    if (c === ",") { row.push(field); field = ""; i++; continue; }
    if (c === "\n" || c === "\r") {
      row.push(field); field = "";
      if (row.length > 1 || row[0] !== "") rows.push(row);
      row = [];
      if (c === "\r" && text[i + 1] === "\n") i++;
      i++; continue;
    }
    field += c; i++;
  }
  if (field !== "" || row.length > 0) { row.push(field); rows.push(row); }
  return rows.filter((r) => r.some((cell) => String(cell).trim() !== ""));
}

function BulkUploadModal({ kind, endpoint, columns, requiredCols, onClose, onDone }) {
  const [text, setText] = useState("");
  const [parsed, setParsed] = useState(null);
  const [errors, setErrors] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);

  const handleFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => setText(String(reader.result || ""));
    reader.readAsText(f);
  };

  const parse = () => {
    setErrors([]);
    setResult(null);
    if (!text.trim()) { setErrors(["Paste some CSV or upload a file first."]); return; }
    const raw = parseCSV(text);
    if (raw.length < 2) { setErrors(["Need a header row + at least one data row."]); return; }
    const headers = raw[0].map((h) => String(h).trim().toLowerCase().replace(/[^a-z0-9]+/g, "_"));
    const issues = [];
    for (const required of requiredCols) {
      if (!headers.includes(required)) issues.push(`Missing required column: "${required}"`);
    }
    const unknown = headers.filter((h) => !columns.includes(h));
    if (unknown.length) issues.push(`Unknown columns will be ignored: ${unknown.join(", ")}`);
    if (requiredCols.some((c) => !headers.includes(c))) {
      setErrors(issues);
      return;
    }
    const rowObjs = raw.slice(1).map((cells) => {
      const obj = {};
      headers.forEach((h, idx) => {
        if (columns.includes(h)) {
          const v = (cells[idx] || "").trim();
          if (v) obj[h] = v;
        }
      });
      return obj;
    });
    setParsed({ headers, rows: rowObjs });
    if (issues.length) setErrors(issues);
  };

  const submit = async () => {
    if (!parsed) return;
    setUploading(true);
    try {
      const { data } = await api.post(endpoint, { rows: parsed.rows });
      setResult(data);
      toast.success(`${data.created} created · ${data.skipped} skipped`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose} data-testid={`crm-${kind}-bulk-modal`}>
      <div className="bg-white rounded-sm p-5 max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-3">
          <div>
            <div className="font-display text-lg font-bold text-slate-900">Bulk upload {kind}</div>
            <div className="text-xs text-slate-500 mt-0.5">
              Paste CSV (or upload a .csv) with these columns: <span className="font-mono text-[10px] bg-slate-100 px-1.5 py-0.5 rounded">{columns.join(", ")}</span>
              <br />Required: <span className="font-mono text-[10px] bg-amber-50 text-amber-700 px-1 rounded">{requiredCols.join(", ")}</span>. Duplicates are skipped silently.
            </div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={16} /></button>
        </div>

        {!result && (
          <>
            <div className="flex items-center gap-2 mb-2">
              <label className="text-xs text-slate-600 inline-flex items-center gap-1.5 cursor-pointer bg-slate-100 hover:bg-slate-200 px-2.5 py-1.5 rounded-sm" data-testid="crm-bulk-file-input-label">
                <Upload size={11} /> Upload .csv
                <input type="file" accept=".csv,text/csv" onChange={handleFile} className="hidden" data-testid="crm-bulk-file-input" />
              </label>
              <span className="text-xs text-slate-400">or paste below</span>
            </div>
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={8}
              placeholder={`${columns.slice(0, 4).join(",")},...
example value 1,foo,bar,baz`}
              className="w-full rounded-sm border border-slate-200 px-2 py-1.5 text-xs font-mono focus:ring-1 focus:ring-[#F54800] focus:border-[#F54800] outline-none resize-none"
              data-testid="crm-bulk-textarea"
            />
            {errors.length > 0 && (
              <ul className="mt-2 text-xs text-amber-700 list-disc list-inside" data-testid="crm-bulk-errors">
                {errors.map((e, i) => <li key={i}>{e}</li>)}
              </ul>
            )}
            <div className="flex justify-end gap-2 mt-3">
              <Button variant="outline" size="sm" onClick={parse} className="rounded-sm h-8" data-testid="crm-bulk-preview">Preview</Button>
              {parsed && (
                <Button size="sm" onClick={submit} disabled={uploading || parsed.rows.length === 0} className="rounded-sm h-8 bg-[#F54800] hover:bg-[#D63E00] text-white" data-testid="crm-bulk-submit">
                  {uploading ? <Loader2 size={11} className="mr-1.5 animate-spin" /> : <Upload size={11} className="mr-1.5" />}
                  Import {parsed.rows.length} row{parsed.rows.length === 1 ? "" : "s"}
                </Button>
              )}
            </div>
            {parsed && (
              <div className="mt-3 flex-1 overflow-auto border border-slate-200 rounded-sm" data-testid="crm-bulk-preview-table">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 sticky top-0">
                    <tr>{parsed.headers.filter((h) => columns.includes(h)).map((h) => <th key={h} className="px-2 py-1.5 text-left">{h}</th>)}</tr>
                  </thead>
                  <tbody>
                    {parsed.rows.slice(0, 50).map((r, i) => (
                      <tr key={i} className="border-t border-slate-100">
                        {parsed.headers.filter((h) => columns.includes(h)).map((h) => <td key={h} className="px-2 py-1 text-slate-700">{r[h] || <span className="text-slate-300">—</span>}</td>)}
                      </tr>
                    ))}
                  </tbody>
                </table>
                {parsed.rows.length > 50 && (
                  <div className="text-[10px] text-slate-400 italic p-2 text-center">… and {parsed.rows.length - 50} more</div>
                )}
              </div>
            )}
          </>
        )}

        {result && (
          <div className="flex-1 overflow-auto" data-testid="crm-bulk-result">
            <div className="text-sm text-slate-900 mb-2">
              <span className="text-emerald-700 font-bold">{result.created}</span> created ·
              <span className="text-amber-700 font-bold ml-1">{result.skipped}</span> skipped
            </div>
            {result.skipped_rows?.length > 0 && (
              <details className="text-xs">
                <summary className="cursor-pointer text-slate-600 hover:text-slate-900 mb-1">Skipped rows ({result.skipped_rows.length})</summary>
                <ul className="space-y-1 max-h-48 overflow-auto">
                  {result.skipped_rows.map((r, i) => (
                    <li key={i} className="text-slate-500">
                      <span className="text-amber-700">{r.reason}:</span> {JSON.stringify(r.row).slice(0, 120)}
                    </li>
                  ))}
                </ul>
              </details>
            )}
            <div className="flex justify-end gap-2 mt-4">
              <Button size="sm" onClick={onDone} className="rounded-sm h-8 bg-[#F54800] hover:bg-[#D63E00] text-white" data-testid="crm-bulk-done">Done</Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


/* -------------------------------------------------------------------------- */
/*  Phase 75 extracted modules                                                 */
/* -------------------------------------------------------------------------- */
// SettingsTab, EditableList, ACTION_TONE, ActivityTimeline, CommandPalette,
// and IntakeTokensCard now live under /app/frontend/src/pages/crm/*.jsx and
// are imported at the top of this file.
